import { useEffect, type ReactNode } from 'react';
import { ErrorBoundary } from './ErrorBoundary';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

/**
 * A lazy route's dynamic import() rejects with this class of error when the
 * requested chunk hash no longer exists — the classic "tab was open across a
 * deploy" case: the stale index.html asks for old asset hashes that nginx now
 * 404s. Recovery is simply loading the fresh index.html.
 */
export function isChunkLoadError(error: Error | null): boolean {
  if (!error) return false;
  return (
    error.name === 'ChunkLoadError' ||
    /dynamically imported module|Loading chunk|Importing a module script failed/i.test(error.message)
  );
}

/** sessionStorage guard so a chunk-error auto-reload can never loop. */
const CHUNK_RELOAD_GUARD = 'grid2:chunk-reloaded';

interface PageErrorFallbackProps {
  error: Error | null;
  onRetry: () => void;
}

function PageErrorFallback({ error, onRetry }: PageErrorFallbackProps) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100 mb-6">
        <AlertTriangle className="h-8 w-8 text-red-600" aria-hidden="true" />
      </div>
      <h1 className="text-xl font-semibold text-gray-900 mb-2">
        Page Error
      </h1>
      <p className="text-sm text-gray-500 max-w-lg mb-6">
        {error?.message ?? 'This page encountered an unexpected error. You can try reloading or go back to the dashboard.'}
      </p>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-blue-600 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
        >
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
          Retry
        </button>
        <a
          href="/"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <Home className="h-4 w-4" aria-hidden="true" />
          Dashboard
        </a>
      </div>
    </div>
  );
}

interface PageErrorBoundaryProps {
  children: ReactNode;
}

/**
 * Page-level error boundary wrapper. Wraps each route's page component
 * to catch render errors and show a full-page fallback with retry and
 * navigation options.
 */
export function PageErrorBoundary({ children }: PageErrorBoundaryProps) {
  return (
    <ErrorBoundary
      fallback={(error) => <PageErrorFallbackWrapper error={error} />}
    >
      {children}
    </ErrorBoundary>
  );
}

/**
 * Wrapper that renders the page fallback with the REAL caught error (the
 * previous version hard-coded `error={null}`, making a stale-chunk deploy
 * error indistinguishable from a genuine render crash — the 2026-07-12
 * "Page Error" report). Chunk-load errors auto-reload ONCE (sessionStorage
 * guarded against loops) to pick up the freshly deployed bundle; anything
 * else renders the fallback with the actual message.
 */
function PageErrorFallbackWrapper({ error }: { error: Error | null }) {
  const chunkError = isChunkLoadError(error);

  useEffect(() => {
    if (!chunkError) return;
    let alreadyReloaded = false;
    try {
      alreadyReloaded = sessionStorage.getItem(CHUNK_RELOAD_GUARD) === '1';
      if (!alreadyReloaded) sessionStorage.setItem(CHUNK_RELOAD_GUARD, '1');
    } catch {
      // sessionStorage unavailable (private-mode edge) — fall through to the
      // manual Retry button rather than risking a reload loop.
      return;
    }
    if (!alreadyReloaded) window.location.reload();
  }, [chunkError]);

  function handleRetry() {
    // Manual retry always clears the guard first so it can reload again.
    try {
      sessionStorage.removeItem(CHUNK_RELOAD_GUARD);
    } catch {
      // ignore — reload regardless
    }
    window.location.reload();
  }

  return (
    <PageErrorFallback
      error={
        chunkError
          ? new Error('A new version of Grid2 was just deployed. Reloading to pick it up…')
          : error
      }
      onRetry={handleRetry}
    />
  );
}
